import { Box, Typography } from '@mui/material';
import { Button, Modal, Select } from 'components/ui';
import React, { useEffect, useState } from 'react';
import { useOrganizationsApi } from 'services/api/hooks/useOrganizationsApi.hook';
import { useSnackbar } from 'notistack';
import { ORGANIZATION_ROLES, OrganizationRole } from 'enum/organization-roles';

interface EditRoleModalProps {
  open: boolean;
  onClose: () => void;
  leaderId?: string;
  currentRole?: string;
  /** When true, PATCH admin endpoint for pending user-initiated leader requests */
  pendingUserInitiatedRequest?: boolean;
  onSuccess?: () => void;
}

export const EditRoleModal: React.FC<EditRoleModalProps> = ({
  open,
  onClose,
  leaderId,
  currentRole,
  pendingUserInitiatedRequest,
  onSuccess,
}) => {
  const [role, setRole] = useState<OrganizationRole | ''>('');
  const organizationsApi = useOrganizationsApi();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (open && currentRole) setRole(currentRole as OrganizationRole);
  }, [open, currentRole]);

  const handleSave = async () => {
    if (!leaderId || !role) return;

    try {
      const result = pendingUserInitiatedRequest
        ? await organizationsApi.adminUpdatePendingLeaderRequestRole(
            leaderId,
            role,
          )
        : await organizationsApi.editLeaderRole(leaderId, role);
      if (!result.success)
        throw new Error(result.error.message || 'Failed to update role');

      enqueueSnackbar('Role updated successfully', { variant: 'success' });
      onSuccess?.();
      onClose();
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Failed to update role';
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  return (
    <Modal title="Edit Organization Role" open={open} onClose={onClose}>
      <Box sx={{ width: { xs: '100%', sm: 400 } }}>
        <Select
          label="Organization Role"
          value={role}
          onChange={e => setRole(e.target.value as OrganizationRole)}
          placeholder="Select an organization role"
          required
        >
          {ORGANIZATION_ROLES.map(option => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </Select>
      </Box>

      <Box
        mt={30}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography
          onClick={onClose}
          sx={{
            textDecoration: 'underline',
            cursor: 'pointer',
            color: 'text.primary',
            '&:hover': {
              color: 'primary.main',
            },
          }}
        >
          Cancel
        </Typography>
        <Button
          sx={{ width: '220px', height: '47px' }}
          variant="vibrant"
          disabled={!role}
          onClick={handleSave}
        >
          Save Changes
        </Button>
      </Box>
    </Modal>
  );
};
